"use client";
import React from 'react';

interface ServiceItem {
  title: string;
  description: string;
  details: string[];
  bgColor: string;
  textColor: string;
  titleColor: string;
  accentColor: string;
}

interface ServiceGridRowProps {
  title?: string;
  description?: string;
  gridItems?: ServiceItem[];
  rowTitle?: string;
  rowDescription?: string;
  rowItems?: ServiceItem[];
}

export default function ServiceGridRow({
  title,
  description,
  gridItems = [],
  rowTitle,
  rowDescription,
  rowItems = []
}: ServiceGridRowProps) {
  const hasGrid = gridItems.length > 0;
  const hasRows = rowItems.length > 0;

  if (!hasGrid && !hasRows) return null;

  // Helper to determine if a string is a hex color or tailwind class
  const getStyle = (val: string, type: 'bg' | 'text' | 'border' | 'color') => {
    if (!val) return {};
    if (val.startsWith('#') || val.startsWith('rgb') || val.startsWith('hsl')) {
      if (type === 'bg') return { backgroundColor: val };
      if (type === 'text' || type === 'color') return { color: val };
      if (type === 'border') return { borderColor: val };
    }
    return {};
  };

  const getClassName = (val: string, defaultClass: string) => {
    if (!val) return defaultClass;
    if (val.startsWith('#') || val.startsWith('rgb') || val.startsWith('hsl')) return '';
    return val;
  };

  return (
    <div className="w-full">
      {/* Grid Part */}
      {hasGrid && (
        <section className={`max-w-7xl container mx-auto px-4 ${hasRows ? 'pt-10 pb-5' : 'py-10'}`}>
          {(title || description) && (
            <div className="text-center mb-10">
              {title && (
                <h2 className="text-3xl font-bold text-gray-800 mb-6" dangerouslySetInnerHTML={{ __html: title }} />
              )}
              {description && (
                <p className="text-lg text-gray-600 max-w-4xl mx-auto" dangerouslySetInnerHTML={{ __html: description }} />
              )}
            </div>
          )}

          <div className="grid md:grid-cols-3 gap-6">
            {gridItems.map((item, index) => (
              <div 
                key={index} 
                className={`p-6 rounded-lg shadow-md flex flex-col border-l-4 ${getClassName(item.bgColor, 'bg-blue-50')} ${getClassName(item.accentColor, 'border-blue-400')}`}
                style={{ 
                  ...getStyle(item.bgColor, 'bg'), 
                  ...getStyle(item.accentColor, 'border') 
                }}
              >
                <h3 
                  className={`text-2xl font-semibold ${getClassName(item.titleColor, 'text-blue-800')}`}
                  style={getStyle(item.titleColor, 'color')}
                >
                  {item.title}
                </h3>
                <p 
                  className={`mt-2 flex-grow ${getClassName(item.textColor, 'text-blue-700')}`}
                  style={getStyle(item.textColor, 'color')}
                >
                  {item.description}
                </p>
                {item.details && item.details.length > 0 && (
                  <ul className="list-disc pl-5 mt-4 text-gray-700 space-y-1">
                    {item.details.map((detail, idx) => (
                      <li key={idx}>{detail}</li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Row Part */}
      {hasRows && (
        <section className={`max-w-7xl container mx-auto px-4 ${hasGrid ? 'pt-5 pb-10' : 'py-10'}`}>
          {(rowTitle || rowDescription) && (
            <div className="text-center mb-10">
              {rowTitle && (
                <h2 className="text-3xl font-bold text-gray-800 mb-6" dangerouslySetInnerHTML={{ __html: rowTitle }} />
              )}
              {rowDescription && (
                <p className="text-lg text-gray-600 max-w-4xl mx-auto" dangerouslySetInnerHTML={{ __html: rowDescription }} />
              )}
            </div>
          )}

          <div className="space-y-8">
            {rowItems.map((item, index) => (
              <div 
                key={index} 
                className={`p-6 rounded-lg shadow-md border-l-4 ${getClassName(item.bgColor, 'bg-blue-50')} ${getClassName(item.accentColor, 'border-blue-400')}`}
                style={{ 
                  ...getStyle(item.bgColor, 'bg'), 
                  ...getStyle(item.accentColor, 'border') 
                }}
              >
                <h3 
                  className={`text-2xl font-semibold ${getClassName(item.titleColor, 'text-blue-800')}`}
                  style={getStyle(item.titleColor, 'color')}
                >
                  {item.title}
                </h3>
                {item.description && (
                  <p 
                    className={`mt-2 ${getClassName(item.textColor, 'text-blue-700')}`}
                    style={getStyle(item.textColor, 'color')}
                  >
                    {item.description}
                  </p>
                )}
                {item.details && item.details.length > 0 && (
                  <ul className="list-disc pl-5 mt-3 text-gray-700 space-y-1">
                    {item.details.map((detail, idx) => (
                      <li key={idx}>{detail}</li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
